import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReactFlowProvider } from "reactflow";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import WorkflowCanvas from "../../../components/workflow/WorkflowCanvas";
import StageConfigPanel from "../../../components/workflow/StageConfigPanel";
import WorkflowToolbar from "../../../components/workflow/WorkflowToolbar";
import SaveAsModal from "../../../components/workflows/SaveAsModal";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import {
  exportWorkflowJSON,
  importWorkflowJSON,
  validateWorkflowJSON,
} from "../../../utils/workflowSchema";
import { defaultStage } from "../../../utils/workflowDefaults";
import { createClientId } from "../../../utils/id";
import { usePermission } from "../../../hooks/usePermission";
import { PERMISSIONS } from "../../../constants/permissions";
import api from "../../../api/client";

export default function WorkflowBuilder() {
  const navigate = useNavigate();
  const { workflowId: routeWorkflowId } = useParams();
  const [searchParams] = useSearchParams();

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [workflowId, setWorkflowId] = useState(routeWorkflowId || null);
  const [workflowName, setWorkflowName] = useState("Untitled Workflow");
  const [workflowDescription, setWorkflowDescription] = useState("");
  const [workflowStatus, setWorkflowStatus] = useState("DRAFT");
  const [workflowVersion, setWorkflowVersion] = useState(1);
  const [unsaved, setUnsaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [leaveConfirm, setLeaveConfirm] = useState(false);
  const [savedVersion, setSavedVersion] = useState(null);
  const canManageCreate = usePermission(PERMISSIONS.WORKFLOW_MANAGE_CREATE);
  const canManageEdit = usePermission(PERMISSIONS.WORKFLOW_MANAGE_EDIT);

  const canvasRef = useRef(null);
  const loadingRef = useRef(false);
  const unsavedRef = useRef(false);
  const dragTrackerRef = useRef({ isDragging: false });

  const markUnsaved = useCallback(() => {
    if (loadingRef.current) return;
    setUnsaved(true);
    unsavedRef.current = true;
  }, []);

  const applyNodes = useCallback((updater) => {
    setNodes((previous) => {
      const next = typeof updater === "function" ? updater(previous) : updater;
      if (!loadingRef.current) {
        setUnsaved(true);
        unsavedRef.current = true;
      }
      return next;
    });
  }, []);

  const applyEdges = useCallback((updater) => {
    setEdges((previous) => {
      const next = typeof updater === "function" ? updater(previous) : updater;
      if (!loadingRef.current) {
        setUnsaved(true);
        unsavedRef.current = true;
      }
      return next;
    });
  }, []);

  // Mode detection + initial load
  useEffect(() => {
    const load = async () => {
      loadingRef.current = true;
      setWorkflowId(routeWorkflowId || null);

      if (routeWorkflowId) {
        try {
          const resp = await api.get(`/workflows/${routeWorkflowId}`);
          const workflow = resp.data;
          const imported = importWorkflowJSON(workflow.definition || {});
          setNodes(imported.nodes || []);
          setEdges(imported.edges || []);
          setWorkflowName(workflow.name || "Untitled Workflow");
          setWorkflowDescription(workflow.description || "");
          setWorkflowStatus(workflow.status || "DRAFT");
          setWorkflowVersion(workflow.version || 1);
        } catch (err) {
          alert(err.response?.data?.message || "Failed to load workflow");
        } finally {
          setSelectedNode(null);
          setUnsaved(false);
          unsavedRef.current = false;
          loadingRef.current = false;
        }
        return;
      }

      const isNew = searchParams.get("new") === "true";
      if (isNew) {
        setNodes([]);
        setEdges([]);
        setWorkflowName(searchParams.get("name") || "Untitled Workflow");
        setWorkflowDescription(searchParams.get("description") || "");
      } else {
        setNodes([]);
        setEdges([]);
        setWorkflowName("Untitled Workflow");
        setWorkflowDescription("");
      }
      setWorkflowStatus("DRAFT");
      setWorkflowVersion(1);
      setSelectedNode(null);
      setUnsaved(false);
      unsavedRef.current = false;
      loadingRef.current = false;
    };

    load();
  }, [routeWorkflowId, searchParams]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const handleAddStage = useCallback(() => {
    if (canvasRef.current?.addNodeAtCenter) {
      const newNode = {
        id: createClientId("stage"),
        type: "stage",
        position: { x: 0, y: 0 },
        data: { ...defaultStage(), name: `Stage ${nodes.length + 1}` },
      };
      canvasRef.current.addNodeAtCenter(newNode);
      markUnsaved();
    }
  }, [nodes.length, markUnsaved]);

  const handleNodeClick = useCallback((evt, node) => {
    setSelectedNode(node);
  }, []);

  const handleNodeDragStart = useCallback(() => {
    dragTrackerRef.current.isDragging = true;
  }, []);

  const handleNodeDragStop = useCallback(() => {
    setTimeout(() => {
      dragTrackerRef.current.isDragging = false;
    }, 100);
  }, []);

  const handleNodeUpdate = useCallback(
    (updated) => {
      applyNodes((list) =>
        list.map((node) =>
          node.id === updated.id ? { ...node, data: updated.data } : node,
        ),
      );
      setSelectedNode(updated);
    },
    [applyNodes],
  );

  const handleDeleteNode = useCallback(
    (nodeId) => {
      applyNodes((list) => list.filter((node) => node.id !== nodeId));
      applyEdges((list) =>
        list.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
      );
      if (selectedNode?.id === nodeId) {
        setSelectedNode(null);
      }
    },
    [applyNodes, applyEdges, selectedNode],
  );

  const handleClosePanel = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleImportFile = useCallback(
    async (file) => {
      if (!file) return;
      try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        const valid = validateWorkflowJSON(parsed);
        if (!valid.ok) {
          alert("Invalid workflow JSON: " + valid.error);
          return;
        }
        // if (
        //   (nodes.length || edges.length) &&
        //   !window.confirm("Replace current canvas with imported workflow?")
        // ) {
        //   return;
        // }
        const imported = importWorkflowJSON(parsed);
        loadingRef.current = true;
        setNodes(imported.nodes || []);
        setEdges(imported.edges || []);
        setWorkflowName(parsed.name || workflowName);
        setSelectedNode(null);
        loadingRef.current = false;
        markUnsaved();
      } catch (err) {
        alert("Invalid JSON file");
      }
    },
    [workflowName, markUnsaved],
  );

  const handleExport = useCallback(() => {
    const json = exportWorkflowJSON({ name: workflowName, nodes, edges });
    const blob = new Blob([JSON.stringify(json, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${workflowName.replace(/\s+/g, "_") || "workflow"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [workflowName, nodes, edges]);

  const handleClear = useCallback(() => {
    setClearConfirm(true);
  }, []);

  const doClear = useCallback(() => {
    setNodes([]);
    setEdges([]);
    setSelectedNode(null);
    markUnsaved();
  }, [markUnsaved]);

  const handleEdgeUpdateLabel = useCallback(
    (edgeId, label) => {
      applyEdges((list) =>
        list.map((edge) => (edge.id === edgeId ? { ...edge, label } : edge)),
      );
    },
    [applyEdges],
  );

  const handleSelectionChange = useCallback((selection) => {
    if (dragTrackerRef.current.isDragging) return;
    setSelectedNode(selection?.nodes?.[0] || null);
  }, []);

  const saveWorkflow = useCallback(
    async ({ asCopy = false, copyName = null, silent = false } = {}) => {
      const payload = {
        name: (copyName || workflowName || "").trim() || "Untitled Workflow",
        description: workflowDescription || null,
        status: asCopy ? "DRAFT" : workflowStatus || "DRAFT",
        definition: exportWorkflowJSON({
          name: (copyName || workflowName || "").trim() || "Untitled Workflow",
          nodes,
          edges,
        }),
      };

      setSaving(true);
      try {
        let savedVersion = workflowVersion;
        if (!workflowId || asCopy) {
          const resp = await api.post("/workflows", payload);
          const created = resp.data;
          if (asCopy) {
            navigate(`/workflow-builder/${created.id}`, { replace: true });
          } else {
            setWorkflowId(created.id);
            navigate(`/workflow-builder/${created.id}`, { replace: true });
          }
          setWorkflowStatus(created.status || "DRAFT");
          savedVersion = created.version || 1;
          setWorkflowVersion(savedVersion);
        } else {
          const resp = await api.put(`/workflows/${workflowId}`, payload);
          const updated = resp.data;
          setWorkflowStatus(updated.status || workflowStatus);
          savedVersion = updated.version || workflowVersion;
          setWorkflowVersion(savedVersion);
        }
        setUnsaved(false);
        unsavedRef.current = false;
        setAutoSavedAt(new Date().toISOString());
        if (!silent) {
          setSavedVersion(savedVersion);
        }
      } catch (err) {
        if (!silent) {
          alert(err.response?.data?.message || "Failed to save workflow");
        }
      } finally {
        setSaving(false);
      }
    },
    [
      workflowId,
      workflowName,
      workflowDescription,
      workflowStatus,
      nodes,
      edges,
      navigate,
      workflowVersion,
    ],
  );

  const handleSave = useCallback(async () => {
    if (!workflowId && !canManageCreate) {
      alert("You do not have permission to create workflows.");
      return;
    }
    if (workflowId && !canManageEdit) {
      alert("You do not have permission to edit this workflow.");
      return;
    }
    await saveWorkflow({ silent: false });
  }, [saveWorkflow, workflowId, canManageCreate, canManageEdit]);

  const handleSaveAs = useCallback(
    async (name) => {
      if (!canManageCreate) {
        alert("You do not have permission to create workflows.");
        return;
      }
      await saveWorkflow({ asCopy: true, copyName: name, silent: false });
      setShowSaveAs(false);
    },
    [saveWorkflow, canManageCreate],
  );

  const handleStatusAction = useCallback(
    async (nextStatus) => {
      if (!workflowId) return;
      if (!canManageEdit) return;
      try {
        const resp = await api.patch(`/workflows/${workflowId}/status`, {
          status: nextStatus,
        });
        setWorkflowStatus(resp.data.status || nextStatus);
      } catch (err) {
        alert(err.response?.data?.message || "Failed to change status");
      }
    },
    [workflowId, canManageEdit],
  );

  const handleBack = useCallback(() => {
    if (unsaved) {
      setLeaveConfirm(true);
    } else {
      navigate("/workflows");
    }
  }, [navigate, unsaved]);

  // Auto-save every 60s for persisted workflows
  useEffect(() => {
    const id = setInterval(() => {
      if (!workflowId) return;
      if (!unsavedRef.current) return;
      saveWorkflow({ silent: true });
    }, 60000);
    return () => clearInterval(id);
  }, [workflowId, saveWorkflow]);

  return (
    <ReactFlowProvider>
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "#eef2f7",
        }}
      >
        <WorkflowToolbar
          workflowId={workflowId}
          name={workflowName}
          setName={(value) => {
            setWorkflowName(value);
            markUnsaved();
          }}
          status={workflowStatus}
          version={workflowVersion}
          unsaved={unsaved}
          saving={saving}
          autoSavedAt={autoSavedAt}
          onSave={handleSave}
          onSaveAs={() => setShowSaveAs(true)}
          onBack={handleBack}
          onStatusAction={handleStatusAction}
          onAdd={handleAddStage}
          onExport={handleExport}
          onImportFile={handleImportFile}
          onClear={handleClear}
          onNameFocus={() => setSelectedNode(null)}
          onNameBlur={() => {}}
          canSave={workflowId ? canManageEdit : canManageCreate}
          canSaveAs={canManageCreate}
          canChangeStatus={canManageEdit}
        />

        <div
          style={{
            flex: 1,
            position: "relative",
            padding: 12,
            display: "flex",
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              borderRadius: 16,
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
              background: "#ffffff",
            }}
          >
            <WorkflowCanvas
              ref={canvasRef}
              nodes={nodes}
              edges={edges}
              setNodes={applyNodes}
              setEdges={applyEdges}
              onNodeClick={handleNodeClick}
              onNodeDelete={handleDeleteNode}
              onNodeDragStart={handleNodeDragStart}
              onNodeDragStop={handleNodeDragStop}
              onEdgeLabelEdit={handleEdgeUpdateLabel}
              onSelectionChange={handleSelectionChange}
            />
          </div>
          <StageConfigPanel
            node={selectedNode}
            onClose={handleClosePanel}
            onSave={handleNodeUpdate}
            onDelete={handleDeleteNode}
          />
        </div>
      </div>
      {showSaveAs && (
        <SaveAsModal
          initialName={`Copy of ${workflowName}`}
          onClose={() => setShowSaveAs(false)}
          onConfirm={handleSaveAs}
        />
      )}
      {clearConfirm && (
        <ConfirmDialog
          title="Clear Canvas"
          message="Clear the canvas? This cannot be undone."
          confirmLabel="Clear"
          confirmClass="btn-danger"
          onConfirm={() => {
            setClearConfirm(false);
            doClear();
          }}
          onClose={() => setClearConfirm(false)}
        />
      )}
      {leaveConfirm && (
        <ConfirmDialog
          title="Unsaved Changes"
          message="You have unsaved changes. Leave anyway?"
          confirmLabel="Leave"
          confirmClass="btn-danger"
          onConfirm={() => {
            setLeaveConfirm(false);
            navigate("/workflows");
          }}
          onClose={() => setLeaveConfirm(false)}
        />
      )}
      {savedVersion !== null && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.48)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 16,
          }}
          onClick={() => setSavedVersion(null)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #dce4f4",
              width: "100%",
              maxWidth: 440,
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 18px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>
                Workflow Saved
              </h2>
              <button
                type="button"
                onClick={() => setSavedVersion(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 18,
                  color: "#6b7280",
                  padding: "2px 6px",
                  borderRadius: 4,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ padding: "16px 18px", color: "#4b5563" }}>
              <p style={{ margin: 0 }}>
                Your workflow has been saved successfully{" "}
                <strong style={{ color: "#0f2d5c" }}>(v{savedVersion})</strong>.
              </p>
            </div>
            <div
              style={{
                padding: "12px 18px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="btn-primary"
                onClick={() => setSavedVersion(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </ReactFlowProvider>
  );
}
